import { AfterViewInit, ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'

import { MatInput } from '@angular/material/input'

import { FieldErrors, FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'

import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService, LokalisoituAlvIlmoitusjakso, LokalisoituYritysmuoto } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { AlvIlmoitusjakso, AsiakashakuAsiakas, AsiakashakuRequest, AsiakashakuResponse, EiYhtiomuotoaYhtiomuoto } from 'app/_jaettu-lemonator/model/asiakas'
import { KirjanpitajanNimitiedot } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { KirjanpitajanTiimiService } from 'app/_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { BehaviorSubject, Subject, takeUntil, takeWhile } from 'rxjs'

interface AsiakashakuAsiakasExpanded extends AsiakashakuAsiakas {
  kirjanpitajanNimi?: string
}
interface AsiakashakuLomake {
  haku: string
  yhtiomuoto: Yritysmuoto | EiYhtiomuotoaYhtiomuoto | null
  alvjakso: AlvIlmoitusjakso | null
  kirjanpitaja: KirjanpitajanNimitiedot | null
}

@Component({
  templateUrl: './asiakashaku.component.html',
  styleUrls: ['./asiakashaku.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakashakuComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatInput, { static: true }) hakuInput: MatInput

  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  readonly kirjanpitajienNimitiedot = signal<KirjanpitajanNimitiedot[]>(undefined)
  tulokset: BehaviorSubject<AsiakashakuAsiakasExpanded[]> = new BehaviorSubject(null)

  lomake: FieldTree<AsiakashakuLomake> = form(signal<AsiakashakuLomake>({ haku: '', yhtiomuoto: null, alvjakso: null, kirjanpitaja: null }))
  fieldErrors: FieldErrors = {}
  paivitaValidaatiot: any = null

  yritysmuodot: LokalisoituYritysmuoto[]
  alvjaksot: LokalisoituAlvIlmoitusjakso[]

  readonly isDev = signal<boolean>(undefined)
  readonly isSuperUser = signal<boolean>(undefined)

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _asiakasService: AsiakasService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _firebase: FirebaseLemonator,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
    private _dateService: DateService,
    private _currencyService: CurrencyService,
  ) {
    this._kirjautunutKayttajaService.kirjanpitajaOnItTiiminJasenObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(onJasen => {
      this.isDev.set(onJasen)
    })

    this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(async kirjanpitaja => {
      if (!kirjanpitaja) {
        return
      }
      this.isSuperUser.set(kirjanpitaja.rooli === KirjanpitajanRooli.SUPER)
    })

    this.alvjaksot = this._asiakasService.alvIlmoitusjaksot
    this.yritysmuodot = this._asiakasService.yritysmuodotJaEiTiedossa

    this._kirjanpitajaService.kirjanpitajienNimitiedotObservable.pipe(takeWhile(nimitiedot => !nimitiedot?.length, true)).subscribe(nimitiedot => {
      nimitiedot.sort((a, b) => a.etunimi.localeCompare(b.etunimi))

      this.kirjanpitajienNimitiedot.set(nimitiedot)
    })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnInit() {}

  ngAfterViewInit(): void {
    // Activate the search input by default
    setTimeout(() => {
      this.hakuInput.focus()
      this._changeDetectorRef.markForCheck()
      this._asiakasService.asetaNykyinenAsiakas(null)
    }, 10)
  }

  // private ibanValidator: ValidatorFn = (c: AbstractControl): ValidationErrors | null => {
  //   // console.log(c.value, this.ibanService.isValidIban(c.value))
  //   if (c && c.value && !this._ibanService.isValidIban(c.value)) {
  //     return { 'iban': true }
  //   }
  //   return null
  // }

  // private bicValidator: ValidatorFn = (c: AbstractControl): ValidationErrors | null => {
  //   // console.log(c.value, this.ibanService.isValidIban(c.value))
  //   if (c && c.value && !this.bicService.validoiBic(c.value)) {
  //     return { 'bic': true }
  //   }
  //   return null
  // }

  async search() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      this.paivitaValidaatiot = {}
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const req: AsiakashakuRequest = {}
      if (this.lomake.haku().value()) {
        req.teksti = this.lomake.haku().value()
      }
      if (this.lomake.yhtiomuoto().value()) {
        req.yhtiomuoto = this.lomake.yhtiomuoto().value()
      }
      if (this.lomake.alvjakso().value()) {
        req.alvjakso = this.lomake.alvjakso().value()
      }
      if (this.lomake.kirjanpitaja().value()) {
        req.kirjanpitaja = this.lomake.kirjanpitaja().value().avain
      }
      const resp = await this._firebase.functionsCall<AsiakashakuRequest, AsiakashakuResponse>('asiakasHaeAsiakasta', req, { timeout: 540 * 1000 })
      if (resp.e || !resp.asiakkaat) {
        throw new Error('Error: ' + resp.e)
      }
      if (this.isDev() && this._currencyService.onkoMerkkijonoKokonaisluku(req.teksti)) {
        resp.asiakkaat.sort((a, b) => a.i - b.i)
      } else {
        resp.asiakkaat.sort((a, b) => {
          const lowera = a.n.toLowerCase()
          const lowerb = b.n.toLowerCase()
          if (lowera !== lowerb) {
            return lowera.localeCompare(lowerb)
          }
          return a.y.localeCompare(b.y)
        })
      }
      if (this.isSuperUser()) {
        const asiakkaatPlusKirjanpitajanNimi = resp.asiakkaat.map(as => {
          const kirjanpitaja = this.kirjanpitajienNimitiedot().find(kn => kn.avain === as.v)

          return Object.assign({ kirjanpitajanNimi: kirjanpitaja ? kirjanpitaja.etunimi + ' ' + kirjanpitaja.sukunimi : 'Ei kirjanpitäjää' }, as)
        })
        this.tulokset.next(asiakkaatPlusKirjanpitajanNimi)
      } else {
        this.tulokset.next(resp.asiakkaat)
      }
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string) {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }
}
